<!-- PriorityCriteriaManager.svelte -->
<script>
	import { tick, onMount } from "svelte";
	import {
		Criteria,
		CriteriaCategorization,
		CriteriaPolarity,
	} from "./UserData.js";
	import { PRIORITY_CRITERIA_CONFIG } from "./PriorityCriteriaConfig.js";

	/*------------- state & props -------------*/
	let newItem = $state("");
	
	// 🔄 Auto-generated from single source of truth
	const prioritySuggestedItemsMap = Object.fromEntries(
		PRIORITY_CRITERIA_CONFIG.map(item => [item.name, item.polarity])
	);

	const prioritySuggestedCriteria = PRIORITY_CRITERIA_CONFIG;

	// Reactive list of names for the UI
	let suggestedItems = $state(Object.keys(prioritySuggestedItemsMap));
	// Counter to track the next available custom emoji (cycling through common ones)
	let nextCustomEmojiIndex = $state(0);
	
	const customEmojis = ["⭐", "🔥", "💡", "🚀", "⚡", "🎨", "🔧", "📊", "🎪", "🌟"];
	
	const getCustomEmoji = () => {
		const emoji = customEmojis[nextCustomEmojiIndex % customEmojis.length];
		nextCustomEmojiIndex = (nextCustomEmojiIndex + 1) % customEmojis.length;
		return emoji;
	};

	// 🔄 Auto-generated emoji mapping from single source of truth
	const autoEmojiMapping = Object.fromEntries(
		PRIORITY_CRITERIA_CONFIG.map(item => [item.name, item.emoji])
	);
	autoEmojiMapping.default = "⭐";

	let {
		userItems = $bindable([]), // ← stays two-way-bindable
		emojiMapping = autoEmojiMapping,
	} = $props();

	/*------------- helpers -------------*/
	const scrollToElement = (selector, block) => {
		const el = document.querySelector(selector);
		if (el) el.scrollIntoView({ behavior: "smooth", block });
	};

	const addItem = async (itemName, doScroll = true) => {
		// Check if the item is already in the list or if the name is empty
		if (
			userItems.some((i) => i.name === itemName) ||
			itemName.trim() === ""
		)
			return;

		// Determine the category based on the number of items in userItems
		const category = userItems.length >= 3 ? "Important" : "Critical";
		// Add the new item to userItems with the appropriate emoji
		const polarity = prioritySuggestedItemsMap[itemName] || "Higher is better";
		const isCustom = !prioritySuggestedItemsMap[itemName];

		let emoji = emojiMapping[itemName];
		if (isCustom) {
			emoji = getCustomEmoji();
			console.log("Assigning custom emoji:", emoji, "to", itemName);
		} else {
			emoji = emojiMapping[itemName] || emojiMapping.default;
		}

		// Find the description for this criteria
		const criteriaData = prioritySuggestedCriteria.find(c => c.name === itemName);
		const description = criteriaData?.description || "";

		userItems = [
			...userItems,
			{
				name: itemName,
				category,
				polarity,
				emoji: emoji, // Store the emoji with the item
				description: description, // Store the description with the item
			},
		];
		// Remove the added item from suggestedItems
		suggestedItems = suggestedItems.filter((i) => i !== itemName);

		if (doScroll) {
			// Wait for DOM update then scroll to end of criteria manager
			await tick();
			const criteriaManager = document.querySelector(".criteria-manager");
			if (criteriaManager) {
				criteriaManager.scrollIntoView({ behavior: "smooth", block: "end" });
			}
		}
	};

	const removeItem = (name) => {
		userItems = userItems.filter((i) => i.name !== name);
		if (!suggestedItems.includes(name)) {
			suggestedItems = [...suggestedItems, name];
		}
	};

	const toggleCategory = (item) => {
		const next = item.category === "Critical" ? "Important" : "Critical";
		userItems = userItems.map((i) =>
			i.name === item.name ? { ...i, category: next } : i,
		);
	};

	const togglePolarity = (item) => {
		const next =
			item.polarity === "Higher is better"
				? "Lower is better"
				: "Higher is better";
		userItems = userItems.map((i) =>
			i.name === item.name ? { ...i, polarity: next } : i,
		);
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		addItem(newItem);
		newItem = "";
	};

	const handleKeydown = (itemName) => (e) => {
		if (e?.key === "Enter") addItem(itemName);
	};

	// Pre-select default criteria for prioritization (Expected Impact and Effort Required)
	const defaultCriteriaIds = ["1", "2"];
	onMount(() => {
		if (userItems.length === 0) {
			const defaultNames = prioritySuggestedCriteria
				.filter((c) => defaultCriteriaIds.includes(c.id))
				.map((c) => c.name);
			defaultNames.forEach(name => addItem(name, false));
		}
	});

	/*------------- side-effects -------------*/
	$effect(() => {
		// Update the Criteria store with full criterion objects
		Criteria.set([...userItems]);

		// For backward compatibility, also update the separate stores
		const categories = userItems.map((i) => i.category);
		const polarities = userItems.map((i) => i.polarity);

		CriteriaCategorization.set(categories);
		CriteriaPolarity.set(polarities);

		// Sync suggestedItems - remove any items that are already in userItems
		const selectedNames = userItems.map((i) => i.name);
		const allSuggestedNames = PRIORITY_CRITERIA_CONFIG.map((c) => c.name);
		suggestedItems = allSuggestedNames.filter((name) => !selectedNames.includes(name));

		console.log("userItems:", $state.snapshot(userItems));
	});
</script>

<div
	class="criteria-manager"
	style="width:85vw;max-width:600px;margin-bottom:20px;"
>
	<p
		style="text-align:center;padding:1.5em;margin-top:-1em;margin-bottom:1em;"
	>
		Choose your <strong>priority factors</strong>
	</p>

	<!-- suggested criteria grid -->
	<div class="grid-container">
		{#each suggestedItems as item (item)}
			{@const criteriaData = prioritySuggestedCriteria.find(c => c.name === item)}
			<div
				class="grid-item"
				title={criteriaData?.description || item}
				role="button"
				tabindex="0"
				onclick={() => addItem(item)}
				onkeydown={handleKeydown(item)}
			>
				<span class="grid-emoji">
					{emojiMapping[item] || emojiMapping.default}
				</span>
				<span class="grid-label">{item}</span>
			</div>
		{/each}
	</div>

	<p style="text-align:center;padding:0.5em;">
		or create <strong>custom ones</strong>
	</p>

	<!-- add-factor form -->
	<div class="add-factor">
		<ul class="add-factor">
			<form onsubmit={handleSubmit} class="add-item-form">
				<input
					type="text"
					bind:value={newItem}
					placeholder="Add priority factor"
				/>
				<button type="submit" class="add-button" aria-label="Add factor"
					>+</button
				>
			</form>
		</ul>
	</div>

	<!-- list of selected criteria -->
	<div class="selected-criteria-section" style="margin-top:60px">
		<ul class="item-list">
			{#each userItems as item (item.name)}
				<li class="item">
					<div class="columns">
						<div
							class="column is-flex is-flex-direction-column is-align-items-center"
							style="margin-right:0.5em;"
						>
							<span class="item-emoji">
								{item.emoji || emojiMapping[item.name] || emojiMapping.default}
							</span>

							<div
								class={item.category === "Critical"
									? "tag is-primary is-light"
									: "tag is-warning is-light"}
								role="button"
								tabindex="0"
								onclick={() => toggleCategory(item)}
								onkeydown={() => toggleCategory(item)}
							>
								{item.category}
							</div>
						</div>
					</div>

					<span style="text-align:center;">{item.name}</span>

					<button
						type="button"
						onclick={() => removeItem(item.name)}
						class="delete is-large"
						aria-label={`Delete ${item.name}`}
					>
					</button>
				</li>
			{/each}
		</ul>
	</div>
</div>

<style>
	.tag {
		margin-top: 0.5em;
	}

	.tag.is-primary {
		background-color: #9cfffa;
		color: #084b83;
	}

	.tag.is-warning {
		background-color: #ffec6c;
	}

	@media (max-width: 600px) {
		/* Make the grid responsive */
		.grid-container {
			grid-template-columns: repeat(2, 1fr);
		}
	}

	.item {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 8px 10px;
		background-color: #fff;
		border-radius: 5px;
		margin-bottom: 10px;
		margin-right: 1em;
		box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
	}

	.item-emoji {
		font-size: 2em;
		margin-bottom: 0.25em;
		user-select: none;
	}

	.grid-container {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 0.5em;
		margin-bottom: 20px;
	}

	.grid-item {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 10px;
		background-color: #fff;
		border-radius: 5px;
		box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
		cursor: pointer;
		min-height: 80px;
	}

	.grid-item:hover {
		background-color: #f0f0f0;
	}

	.grid-item:focus {
		outline: 2px solid #9cfffa;
	}

	.grid-emoji {
		font-size: 2em;
		margin-bottom: 0.25em;
		user-select: none;
	}

	.grid-label {
		font-size: 0.75em;
		text-align: center;
		line-height: 1.2;
		font-weight: 500;
	}

	.add-item-form {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 8px 10px;
		background-color: #fff;
		border-radius: 5px;
		margin-bottom: 10px;
		box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
	}

	input[type="text"] {
		width: 90%;
		padding: 10px;
		margin-bottom: 10px;
	}

	ul {
		width: 100%;
		list-style-type: none;
		padding: 0;
	}

	button.add-button,
	button.delete {
		margin-left: -0.8em;
		border-radius: 7px;
	}

	button.add-button {
		color: #084b83;
	}

	.add-item-form:hover {
		background-color: #f0f0f0;
	}
</style>
